import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
  Link,
  useToastController,
} from '@fluentui/react-components';
import { GLOBAL_TOASTER_ID } from './ThemedProvider.js';
import { refreshUsageSoon } from '../stores/usageStore.js';

interface QuotaExceededDetail {
  code: 'QUOTA_EXCEEDED';
  message: string;
  details?: {
    counter: 'projects_created' | 'chats_sent' | 'concepts_quoted' | 'apus_generated';
    current: number;
    limit: number | null;
    plan: string;
  };
}

const COOLDOWN_MS = 30 * 1000; // 30s entre toasts del mismo counter

const COUNTER_LABEL: Record<string, string> = {
  projects_created: 'proyectos',
  chats_sent: 'chats',
  concepts_quoted: 'conceptos cotizados',
  apus_generated: 'APUs generados con inteligencia artificial',
};

/**
 * Escucha el evento global `opus:quota-exceeded` (disparado por `apiFetch`
 * cuando el backend responde 403 QUOTA_EXCEEDED). Muestra un toast
 * informativo con CTA a `/account`. Aplica cooldown por counter para no
 * spammear si el usuario re-intenta varias veces.
 *
 * También refresca el usage store para que el medidor refleje el estado
 * real (el contador del servidor está adelantado vs lo que vemos en UI).
 */
export function QuotaExceededListener() {
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);
  const navigate = useNavigate();
  const lastShownAt = useRef<Record<string, number>>({});

  useEffect(() => {
    function onQuota(ev: Event) {
      const detail = (ev as CustomEvent<QuotaExceededDetail | undefined>).detail;
      console.warn('[QUOTA_EXCEEDED]', detail);
      const counter = detail?.details?.counter ?? 'unknown';
      const now = Date.now();
      const prev = lastShownAt.current[counter] ?? 0;
      if (now - prev < COOLDOWN_MS) return;
      lastShownAt.current[counter] = now;

      const label = COUNTER_LABEL[counter] ?? counter;
      const limit = detail?.details?.limit;
      const plan = detail?.details?.plan ?? '—';

      dispatchToast(
        <Toast>
          <ToastTitle>Cuota mensual alcanzada</ToastTitle>
          <ToastBody>
            {detail?.message ??
              `Tu plan ${plan} permite ${limit ?? '?'} ${label} al mes y ya consumiste el límite.`}
          </ToastBody>
          <ToastFooter>
            <Link onClick={() => navigate('/account')}>Ver detalles</Link>
          </ToastFooter>
        </Toast>,
        { intent: 'warning', timeout: 8_000 },
      );

      // Refresca el medidor en caso de que esté desactualizado.
      refreshUsageSoon();
    }
    window.addEventListener('opus:quota-exceeded', onQuota);
    return () => window.removeEventListener('opus:quota-exceeded', onQuota);
  }, [dispatchToast, navigate]);

  return null;
}
